import { Fragment, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { NvExpandIcon } from "@/components/navigator";
import {
  NvArrowRightIcon,
  NvBoxIcon,
  NvCalendarIcon,
  NvCheckSmallIcon,
  NvCopyIcon,
  NvFileIcon,
  NvFileOkIcon,
  NvFlagIcon,
  NvGlobeIcon,
  NvLockOutlineIcon,
  NvMonitorIcon,
  NvPersonOutlineIcon,
  NvPriceTagIcon,
  NvPulseIcon,
  NvServerIcon,
  NvShieldCheckIcon,
  NvStackDocIcon,
  NvUsersIcon,
  NvVideoIcon,
} from "@/components/navigator/nv-icons-internal";
import { cn } from "@/lib/utils";
import type {
  InternalAdoption,
  InternalComposition,
  InternalCompositionNode,
  InternalDemo,
  InternalDetailData,
  InternalPolicy,
  InternalUsecase,
  InternalWorkflow,
} from "@/services/navigator/internal-detail-types";

import { isHuman, WorkflowFullDialog } from "./workflow-diagram";
import { InternalSection, SubHeading } from "./internal-parts";
import type { WorkflowStage, WorkflowStep } from "@/services/navigator/agent-detail-service";

// 워크플로우 · 조합 구성 · 시연/데모 · 유즈케이스 · 도입 조건 · 구현과 확산 · 운영과 사업 정책 (Figma ver.7 03-09~12 · 03-10-1)

const ARROW = (
  <span aria-hidden className="text-[20px] leading-6 text-nv-text-tertiary">
    →
  </span>
);

// ── 워크플로우 ──
const PER_ROW = 3;

// 같은 앞 숫자(3-1, 3-2)는 같은 열에 쌓아 분기
function columnsOf(steps: WorkflowStep[]): WorkflowStep[][] {
  const out: WorkflowStep[][] = [];
  for (const step of steps) {
    const key = step.code.split("-")[0];
    const last = out[out.length - 1];
    if (last && last[0].code.includes("-") && last[0].code.split("-")[0] === key && step.code.includes("-")) last.push(step);
    else out.push([step]);
  }
  return out;
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

function FlowNode({ step, selected, onSelect }: { step: WorkflowStep; selected: boolean; onSelect: () => void }) {
  const human = isHuman(step);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex min-h-[96px] w-full flex-col items-start rounded-[12px] border px-[17px] pb-4 pt-4 text-left",
        human ? "border-[#D9DCDF] bg-[#EEEFF1]" : "border-nv-brand bg-nv-brand-subtle",
        selected && "border-2 px-4 pt-[15px]",
      )}
    >
      <span className={cn("text-[13px] font-medium leading-5", human ? "text-nv-text-tertiary" : "text-nv-brand-text")}>{step.code}</span>
      <span className="mt-[7px] break-keep text-[15px] font-medium leading-[22px] text-nv-text-primary">{step.name}</span>
      {human && <span className="mt-[6px] inline-flex h-[23px] items-center rounded-[8px] border border-nv-border bg-white px-2 text-[11px] leading-[17px] text-nv-text-secondary">사람 {step.hitl}</span>}
      {step.loop_back.length > 0 && <span className="mt-1 text-[11px] leading-[14px] text-nv-text-tertiary">↩ {step.loop_back.join(", ")}번 단계로 되돌아감</span>}
    </button>
  );
}

function FlowStage({ stage, selected, onSelect, first }: { stage: WorkflowStage; selected: string; onSelect: (code: string) => void; first: boolean }) {
  const rows = useMemo(() => chunk(columnsOf(stage.steps), PER_ROW), [stage.steps]);
  return (
    <div className={cn(!first && "mt-5 border-t border-dashed border-[#DCDFE3] pt-[21px]", first && "mt-[25px]")}>
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-1">
        <span className="inline-flex h-6 items-center rounded-[8px] border border-nv-text-primary bg-white px-2 text-[12px] font-medium leading-[18px] text-nv-text-primary">{stage.no}단계</span>
        <span className="text-[16px] font-medium leading-6 text-nv-text-primary">{stage.name}</span>
        {stage.description && <span className="text-[13px] leading-5 text-nv-text-tertiary">{stage.description}</span>}
      </div>
      <div className="mt-[15px] flex flex-col gap-3">
        {rows.map((row, r) => (
          <div key={r} className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-0">
            {row.map((column, c) => (
              <Fragment key={column[0].code}>
                <div className="flex w-full min-w-0 flex-col gap-2 lg:w-[330px]">
                  {column.map((step) => (
                    <FlowNode key={step.code} step={step} selected={step.code === selected} onSelect={() => onSelect(step.code)} />
                  ))}
                </div>
                {c < row.length - 1 && <div className="hidden w-11 shrink-0 items-center justify-center lg:flex">{ARROW}</div>}
              </Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function FlowDetail({ step }: { step: WorkflowStep }) {
  const fields = [
    { label: "INPUT", value: step.input },
    { label: "수행업무", value: step.does },
    { label: "OUTPUT", value: step.output },
  ].filter((f) => f.value);
  return (
    <div className="mt-5 grid gap-x-6 gap-y-4 rounded-[12px] border border-nv-border bg-white px-7 py-[22px] lg:grid-cols-[208px_250px_250px_minmax(0,1fr)]">
      <h4 className="flex items-baseline gap-2 text-[17px] font-medium leading-[26px] text-nv-text-primary">
        <span className="text-nv-brand-text">{step.code}</span>
        {step.name}
      </h4>
      {fields.map((f) => (
        <div key={f.label} className="flex min-w-0 flex-col gap-[7px]">
          <span className="text-[13px] leading-5 text-nv-text-tertiary">{f.label}</span>
          <p className="whitespace-pre-line text-[15px] leading-6 text-nv-text-primary">{f.value}</p>
        </div>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-[13px] leading-5 text-nv-text-secondary">
      <span className="inline-flex items-center gap-[6px]">
        <span aria-hidden className="h-[14px] w-[14px] rounded-[4px] border-[1.5px] border-nv-brand bg-nv-brand-subtle" />
        Agent 수행
      </span>
      <span className="inline-flex items-center gap-[6px]">
        <span aria-hidden className="h-[14px] w-[14px] rounded-[4px] border border-[#D9DCDF] bg-[#EEEFF1]" />
        사람 개입
      </span>
    </div>
  );
}

function IoColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-[14px]">
      <h3 className="text-[16px] font-medium leading-6 text-nv-text-primary">{title}</h3>
      {children}
    </div>
  );
}

function IoBlocks({ data, agentName }: { data: InternalWorkflow; agentName: string }) {
  const { inputs, outputs, systems } = data;
  if (!inputs.length && !outputs.length && !systems.length) return null;
  const parts: string[] = [];
  if (inputs.length) parts.push("minmax(0,392fr)");
  if (inputs.length && systems.length) parts.push("28px");
  if (systems.length) parts.push("minmax(0,296fr)");
  if (systems.length && outputs.length) parts.push("28px");
  if (outputs.length) parts.push("minmax(0,392fr)");
  return (
    <div className="grid items-start gap-7 lg:gap-0 lg:[grid-template-columns:var(--io-cols)]" style={{ ["--io-cols" as string]: parts.join(" ") }}>
      {inputs.length > 0 && (
        <IoColumn title="입력 데이터">
          <ul className="overflow-hidden rounded-[16px] border border-nv-border bg-white">
            {inputs.map((x, i) => (
              <li key={`${x.name}-${i}`} className={cn("px-5 pb-[17px] pt-4", i > 0 && "border-t border-nv-border")}>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-[15px] font-medium leading-6 text-nv-text-primary">{x.name}</span>
                  {x.required && <span className={cn("shrink-0 pt-[2px] text-[12px] leading-5", x.required === "필수" ? "text-nv-brand-text" : "text-nv-text-tertiary")}>{x.required}</span>}
                </div>
                {x.spec && <p className="mt-[2px] text-[13px] leading-5 text-nv-text-secondary">{x.spec}</p>}
                {x.note && <p className="mt-1 text-[12px] leading-[18px] text-nv-text-tertiary">{x.note}</p>}
              </li>
            ))}
          </ul>
        </IoColumn>
      )}
      {inputs.length > 0 && systems.length > 0 && (
        <div aria-hidden className="hidden items-center justify-center self-center lg:flex">
          {ARROW}
        </div>
      )}
      {systems.length > 0 && (
        <IoColumn title="연동 시스템">
          <div className="flex flex-col gap-[10px] rounded-[16px] bg-nv-bg-page p-4">
            <div className="flex min-h-[52px] items-center justify-center rounded-[12px] bg-nv-surface-inverse px-3 text-center text-[15px] font-medium leading-6 text-white">{agentName}</div>
            {systems.map((s, i) => (
              <div key={`${s.name}-${i}`} className="flex flex-col rounded-[10px] border border-nv-border bg-white px-[14px] pb-[11px] pt-[10px]">
                <span className="text-[14px] font-medium leading-[22px] text-nv-text-primary">{s.name}</span>
                {s.text && <span className="text-[12px] leading-[18px] text-nv-text-secondary">{s.text}</span>}
              </div>
            ))}
          </div>
        </IoColumn>
      )}
      {systems.length > 0 && outputs.length > 0 && (
        <div aria-hidden className="hidden items-center justify-center self-center lg:flex">
          {ARROW}
        </div>
      )}
      {outputs.length > 0 && (
        <IoColumn title="산출물">
          <ul className="overflow-hidden rounded-[16px] bg-nv-brand-subtle">
            {outputs.map((x, i) => (
              <li key={`${x.name}-${i}`} className={cn("px-5 pb-[17px] pt-4", i > 0 && "border-t border-[#F6D9C7]")}>
                <div className="flex min-h-[27px] items-center justify-between gap-3">
                  <span className="text-[15px] font-medium leading-6 text-nv-text-primary">{x.name}</span>
                  {x.delivery && <span className={cn("inline-flex h-[27px] shrink-0 items-center rounded-[8px] bg-white px-3 text-[11px] leading-[17px]", x.delivery === "담당자 다운로드" ? "text-nv-brand-text" : "text-nv-text-secondary")}>{x.delivery}</span>}
                </div>
                {x.spec && <p className="mt-1 text-[13px] leading-5 text-nv-text-secondary">{x.spec}</p>}
                {x.text && <p className="mt-1 text-[12px] leading-[18px] text-nv-text-tertiary">{x.text}</p>}
              </li>
            ))}
          </ul>
        </IoColumn>
      )}
    </div>
  );
}

export function WorkflowSection({ data, agentName }: { data: InternalWorkflow; agentName: string }) {
  const first = data.stages[0]?.steps[0]?.code ?? "";
  const [selected, setSelected] = useState(first);
  const [full, setFull] = useState(false);
  const step = data.stages.flatMap((s) => s.steps).find((s) => s.code === selected);
  return (
    <InternalSection id="workflow" title="워크플로우" gap={24}>
      <div className="flex flex-col gap-12">
        {data.stages.length > 0 && (
          <div className="flex flex-col rounded-[20px] bg-nv-bg-page p-7">
            <div className="flex items-center justify-between gap-4">
              <Legend />
              <button type="button" onClick={() => setFull(true)} aria-label="워크플로우 전체 보기" className="inline-flex h-11 w-11 items-center justify-center rounded-[12px] border border-nv-border bg-white text-nv-text-primary hover:border-nv-border-strong">
                <NvExpandIcon size={16} />
              </button>
            </div>
            {data.stages.map((stage, i) => (
              <FlowStage key={stage.no} stage={stage} selected={selected} onSelect={setSelected} first={i === 0} />
            ))}
            {step && <FlowDetail step={step} />}
            {(data.hitl_chips.length > 0 || data.hitl_note) && (
              <div className="mt-5 flex flex-col">
                <h4 className="text-[14px] font-medium leading-[22px] text-nv-text-primary">사람 개입 지점</h4>
                {data.hitl_chips.length > 0 && (
                  <div className="mt-[10px] flex flex-wrap gap-2">
                    {data.hitl_chips.map((chip) => (
                      <span key={chip} className="inline-flex h-8 items-center rounded-[8px] border border-nv-border bg-white px-3 text-[13px] leading-5 text-nv-text-primary">
                        {chip}
                      </span>
                    ))}
                  </div>
                )}
                {data.hitl_note && <p className="mt-[10px] whitespace-pre-line text-[14px] leading-[22px] text-nv-text-secondary">{data.hitl_note}</p>}
              </div>
            )}
          </div>
        )}
        <IoBlocks data={data} agentName={agentName} />
      </div>
      {data.stages.length > 0 && <WorkflowFullDialog open={full} onOpenChange={setFull} agentName={agentName} stages={data.stages} hitlChips={data.hitl_chips} hitlNote={data.hitl_note} />}
    </InternalSection>
  );
}

// ── 조합 구성 (Multi Agent 여부 = 예 + 조합 노드 1개 이상) ──
function Tag({ tone, children }: { tone: "gray" | "brand" | "white"; children: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-[19px] shrink-0 items-center rounded-[6px] px-2 text-[11px] font-bold leading-[15px]",
        tone === "gray" && "bg-[#EEF0F2] text-nv-text-secondary",
        tone === "brand" && "bg-nv-brand text-white",
        tone === "white" && "bg-white text-nv-text-secondary",
      )}
    >
      {children}
    </span>
  );
}

function nodeColumns(nodes: InternalCompositionNode[]) {
  const out: InternalCompositionNode[][] = [];
  for (const n of nodes) {
    const key = n.no.split("-")[0];
    const last = out[out.length - 1];
    if (last && n.no.includes("-") && last[0].no.includes("-") && last[0].no.split("-")[0] === key) last.push(n);
    else out.push([n]);
  }
  return out;
}

function ComboNode({ node, selected, onSelect }: { node: InternalCompositionNode; selected: boolean; onSelect: () => void }) {
  const core = node.kind === "핵심";
  const human = node.kind === "사람 확인";
  const small = node.kind === "연계";
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full flex-col rounded-[12px] border text-left",
        small ? "min-h-[68px] px-[17px] py-[10px]" : "min-h-[144px] px-[17px] py-4",
        selected ? "border-2 border-nv-text-primary px-4" : core ? "border-[#FFD9C2]" : "border-nv-border",
        core ? "bg-nv-brand-subtle" : human ? "bg-[#ECEEF1]" : "bg-white",
        small && selected && "py-[9px]",
        !small && selected && "py-[15px]",
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-medium leading-[17px] text-nv-text-tertiary">{node.no}</span>
        <Tag tone={core ? "brand" : human ? "white" : "gray"}>{node.kind}</Tag>
      </span>
      <span className="mt-[6px] break-keep text-[14px] font-bold leading-5 text-nv-text-primary">{node.name}</span>
      {!small && node.note && <span className="mt-auto pt-3 text-[12px] leading-[17px] text-nv-text-secondary">{node.note}</span>}
    </button>
  );
}

export function CompositionSection({ data }: { data: InternalComposition }) {
  const [selected, setSelected] = useState(data.nodes[0]?.no ?? "");
  const columns = useMemo(() => nodeColumns(data.nodes), [data.nodes]);
  const node = data.nodes.find((n) => n.no === selected) ?? data.nodes[0];
  const fields = [
    { label: "INPUT", value: node.input },
    ...(node.kind === "핵심" || node.task ? [{ label: "수행업무", value: node.task }] : []),
    { label: "OUTPUT", value: node.output },
  ].filter((f) => f.value);
  const three = fields.length === 3;
  return (
    <InternalSection id="composition" title="조합 구성" gap={28}>
      <div className="flex flex-col rounded-[20px] bg-nv-bg-page p-7">
        {data.role && (
          <span className="inline-flex h-8 items-center gap-2 self-end rounded-full border border-nv-border bg-white px-[15px]">
            <span className="text-[12px] font-medium leading-[17px] text-nv-text-tertiary">이 Agent의 역할</span>
            <span className="text-[13px] font-bold leading-[18px] text-nv-text-primary">{data.role}</span>
          </span>
        )}
        <div className={cn("overflow-hidden rounded-[12px] border border-nv-border bg-white", data.role && "mt-5")}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-4">
            <Tag tone="gray">0 총괄 조율</Tag>
            <span className="text-[15px] font-bold leading-[21px] text-nv-text-primary">{data.head.name}</span>
            <Tag tone="brand">현재 Agent</Tag>
            {data.head.text && <span className="text-[13px] leading-[19px] text-nv-text-tertiary">{data.head.text}</span>}
          </div>
          <div className="bg-[#F0F2F5] px-5 pb-5 pt-4">
            <span className="block text-[11px] font-medium leading-[13px] text-[#6B7078]">병렬 수행</span>
            <div className="mt-[11px] flex items-stretch overflow-x-auto">
              {columns.map((col, c) => (
                <Fragment key={col[0].no}>
                  <div className="flex min-w-[160px] flex-1 basis-0 flex-col gap-2">
                    {col.map((n) => (
                      <ComboNode key={n.no} node={n} selected={n.no === node.no} onSelect={() => setSelected(n.no)} />
                    ))}
                  </div>
                  {c < columns.length - 1 && <div className="flex w-8 shrink-0 items-center justify-center">{ARROW}</div>}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className={cn("mt-5 grid gap-x-6 gap-y-3 rounded-[12px] border border-nv-border bg-white px-6 py-5", three ? "lg:grid-cols-[224px_246px_246px_minmax(0,1fr)]" : "lg:grid-cols-[224px_381px_minmax(0,1fr)]")}>
          <div className="flex flex-col">
            <span className="flex items-center gap-[10px]">
              <span className="text-[16px] font-bold leading-[22px] text-nv-brand-text">{node.no}</span>
              <Tag tone={node.kind === "핵심" ? "brand" : "gray"}>{node.kind}</Tag>
            </span>
            <span className="mt-[6px] text-[16px] font-bold leading-[22px] text-nv-text-primary">{node.name}</span>
            {node.agent_id && (
              <Link to={`/agents/${node.agent_id}`} className="mt-[6px] text-[13px] font-medium leading-5 text-nv-brand-text hover:underline">
                Agent 상세 보기 →
              </Link>
            )}
          </div>
          {fields.map((f) => (
            <div key={f.label} className="flex min-w-0 flex-col gap-[4px]">
              <span className="text-[12px] font-medium leading-[17px] text-nv-text-tertiary">{f.label}</span>
              <span className="text-[14px] leading-[21px] text-nv-text-primary">{f.value}</span>
            </div>
          ))}
        </div>
        {data.checkpoint && (
          <div className="mt-5 flex flex-col">
            <h4 className="text-[14px] font-medium leading-5 text-nv-text-primary">진행 확인 지점</h4>
            <p className="mt-2 whitespace-pre-line text-[14px] leading-[22px] text-nv-text-secondary">{data.checkpoint}</p>
          </div>
        )}
      </div>
    </InternalSection>
  );
}

// ── 시연/데모 (MVP 이상) ──
const MATERIAL_ICONS = { live: NvMonitorIcon, video: NvVideoIcon, mockup: NvFileIcon } as const;

function IconBox({ children, tone = "white" }: { children: ReactNode; tone?: "white" | "brand" }) {
  return <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-nv-brand", tone === "white" ? "bg-white" : "bg-nv-brand-subtle")}>{children}</span>;
}

function IoChips({ chips, accentLast }: { chips: string[]; accentLast?: boolean }) {
  if (!chips.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-[6px]">
      {chips.map((c, i) => {
        const accent = accentLast && i === chips.length - 1;
        return (
          <span key={c} className={cn("inline-flex h-[26px] items-center rounded-[6px] border bg-white px-2 text-[12px] leading-[18px]", accent ? "border-[#FFD9C2] font-bold text-nv-brand-text" : "border-nv-border font-medium text-nv-text-secondary")}>
            {c}
          </span>
        );
      })}
    </div>
  );
}

export function DemoSection({ data, production }: { data: InternalDemo; production: boolean }) {
  const hasCard = data.materials.length > 0 || !!data.verify;
  const usageIcons = [NvUsersIcon, NvCopyIcon, NvCalendarIcon];
  return (
    <InternalSection id="demo" title={production ? "시연/데모와 실사용 현황" : "시연/데모"} gap={10}>
      <div className="flex flex-col gap-[10px]">
        {hasCard && (
          <div className="rounded-[16px] border border-nv-border bg-white p-[28px]">
            {data.materials.length > 0 && (
              <>
                <SubHeading className="leading-[27px]">시연/데모 자료</SubHeading>
                <ul className="mt-5 grid gap-3 md:grid-cols-2">
                  {data.materials.map((m) => {
                    const Icon = MATERIAL_ICONS[m.kind];
                    return (
                      <li key={m.kind} className="flex min-h-[153px] flex-col rounded-[12px] bg-nv-bg-page p-5">
                        <div className="flex items-center justify-between">
                          <IconBox>
                            <Icon />
                          </IconBox>
                          <span className="inline-flex h-[23px] items-center rounded-full bg-white pl-3 pr-[10px] text-[12px] font-bold leading-[17px] text-nv-brand-text">
                            <NvCheckSmallIcon />
                            {m.status}
                          </span>
                        </div>
                        <span className="mt-[14px] text-[16px] font-bold leading-6 text-nv-text-primary">{m.title}</span>
                        <span className="mt-[14px] text-[14px] leading-[21px] text-nv-text-secondary">{m.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {data.verify && (
              <div className={cn("flex items-center gap-[14px]", data.materials.length > 0 && "mt-5 border-t border-nv-border pt-5")}>
                <IconBox tone="brand">
                  <NvPulseIcon />
                </IconBox>
                <div className="flex min-w-0 flex-col">
                  <span className="text-[13px] font-medium leading-5 text-nv-text-tertiary">동작 확인 방법</span>
                  <span className="text-[15px] leading-[23px] text-nv-text-primary">{data.verify}</span>
                </div>
              </div>
            )}
          </div>
        )}
        {data.io && (
          <div className="rounded-[16px] border border-nv-border bg-white px-[28px] pb-[18px] pt-[28px]">
            <SubHeading className="leading-[27px]">입출력 예시</SubHeading>
            <div className="mt-5 flex flex-col items-stretch gap-3 lg:flex-row">
              <div className="flex min-h-[187px] flex-1 flex-col rounded-[12px] bg-nv-bg-page p-5">
                <div className="flex items-center justify-between">
                  <IconBox>
                    <NvFileIcon />
                  </IconBox>
                  <span className="inline-flex h-6 items-center rounded-[6px] border border-nv-border bg-white px-[9px] text-[12px] font-medium leading-[18px] text-nv-text-secondary">입력</span>
                </div>
                <span className="mt-4 text-[16px] font-bold leading-6 text-nv-text-primary">{data.io.input.text}</span>
                <IoChips chips={data.io.input.chips} />
              </div>
              <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-full border border-nv-border bg-white text-nv-brand">
                <NvArrowRightIcon />
              </span>
              <div className="flex min-h-[187px] flex-1 flex-col rounded-[12px] border border-[#FFD9C2] bg-nv-brand-subtle p-5">
                <div className="flex items-center justify-between">
                  <IconBox>
                    <NvFileOkIcon />
                  </IconBox>
                  <span className="inline-flex h-[22px] items-center rounded-[6px] bg-nv-brand px-2 text-[12px] font-medium leading-[18px] text-white">결과</span>
                </div>
                <span className="mt-4 text-[16px] font-bold leading-6 text-nv-text-primary">{data.io.result.text}</span>
                <IoChips chips={data.io.result.chips} accentLast />
              </div>
            </div>
          </div>
        )}
        {data.usage.length > 0 && (
          <>
            <SubHeading className="leading-[29px]">실사용 현황</SubHeading>
            <ul className="mt-[3px] grid gap-x-14 rounded-[16px] border border-nv-border bg-white px-8 md:grid-cols-3">
              {data.usage.map((u, i) => {
                const Icon = usageIcons[i % usageIcons.length];
                return (
                  <li key={i} className="relative flex items-center gap-4 py-[34px]">
                    {i > 0 && <span aria-hidden className="absolute -left-7 top-1/2 hidden h-[58px] w-px -translate-y-1/2 bg-nv-border md:block" />}
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-nv-brand-subtle text-nv-brand">
                      <Icon />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="text-[28px] font-bold leading-9 text-nv-text-primary">{u.title}</span>
                      {u.text && <span className="text-[14px] leading-[21px] text-nv-text-secondary">{u.text}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </InternalSection>
  );
}

// ── 유즈케이스 ──
export function UsecasesSection({ data }: { data: InternalUsecase[] }) {
  return (
    <InternalSection id="usecases" title="유즈케이스">
      <div className="flex flex-col gap-4">
        {data.map((u, i) => (
          <article key={i} className="rounded-[16px] border border-nv-border bg-white p-8">
            <span className="block text-[13px] leading-[21px] text-nv-text-tertiary">Use Case {i + 1}</span>
            <h3 className="mt-2 text-[20px] font-medium leading-[29px] text-nv-text-primary">{u.title}</h3>
            <div className="mt-6 flex flex-col items-stretch gap-3 lg:flex-row lg:gap-0">
              {(
                [
                  ["상황", u.situation, false],
                  ["활용 방식", u.usage, false],
                  ["결과", u.result, true],
                ] as const
              ).map(([label, text, accent], k) => (
                <div key={label} className="flex items-stretch lg:contents">
                  <div className={cn("flex min-h-[130px] flex-1 flex-col rounded-[12px] p-6", accent ? "bg-nv-brand-subtle" : "bg-nv-bg-page")}>
                    <span className={cn("text-[14px] font-medium leading-[22px]", accent ? "text-nv-brand-text" : "text-nv-text-secondary")}>{label}</span>
                    <span className="mt-[10px] text-[15px] leading-[25px] text-nv-text-primary">{text}</span>
                  </div>
                  {k < 2 && (
                    <span aria-hidden className="hidden w-10 shrink-0 items-center justify-center text-[22px] leading-none text-nv-text-tertiary lg:flex">
                      →
                    </span>
                  )}
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    </InternalSection>
  );
}

// ── 도입 조건 ──
export function AdoptionSection({ data }: { data: InternalAdoption }) {
  return (
    <InternalSection id="adoption" title="도입 조건">
      {data.cards.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {data.cards.map((c) => (
            <section key={c.no} className="flex flex-col rounded-[16px] border border-nv-border bg-white px-7 py-7">
              <h3 className="flex items-center gap-3 text-[18px] font-medium leading-[26px] text-nv-text-primary">
                <span className="text-[22px] text-[#C9CCD1]">{c.no}</span>
                {c.title}
              </h3>
              <ul className="mt-4 flex flex-col gap-[10px]">
                {c.items.map((item, i) => (
                  <li key={i} className="flex gap-[10px] text-[15px] leading-6 text-nv-text-secondary">
                    <span aria-hidden className="w-[6px] shrink-0 text-nv-brand-text">
                      •
                    </span>
                    <span className="min-w-0">{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      {data.expansion && (
        <div className={cn("flex flex-wrap items-center gap-x-5 gap-y-1 rounded-[12px] bg-nv-bg-page px-6 py-[18px]", data.cards.length > 0 && "mt-4")}>
          <span className="text-[15px] font-medium leading-6 text-nv-text-primary">확산 계획</span>
          <span className="text-[15px] leading-6 text-nv-text-secondary">{data.expansion}</span>
        </div>
      )}
    </InternalSection>
  );
}

// ── 구현과 확산 고려사항 (Pilot 이상) ──
function implementationIcon(title: string | null) {
  if (/GPU|서버|인프라|증설|성능/.test(title ?? "")) return NvServerIcon;
  if (/언어|다국어|글로벌|해외/.test(title ?? "")) return NvGlobeIcon;
  return NvStackDocIcon;
}

export function ImplementationSection({ items }: { items: NonNullable<InternalDetailData["implementation"]> }) {
  return (
    <InternalSection id="implementation" title="구현과 확산 고려사항" gap={10}>
      <ul className="flex flex-col gap-3 rounded-[16px] bg-white p-7">
        {items.map((it, i) => {
          const Icon = implementationIcon(it.title);
          return (
            <li key={i} className="flex min-h-[107px] items-center gap-4 rounded-[12px] bg-nv-bg-page px-5 py-[30px]">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white text-nv-brand">
                <Icon size={i === 2 ? 20 : 20} />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="flex items-center gap-2">
                  <span className="text-[13px] font-bold leading-5 text-nv-brand-text">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] font-bold leading-[23px] text-nv-text-primary">{it.title}</span>
                </span>
                {it.text && <span className="text-[14px] leading-[21px] text-nv-text-secondary">{it.text}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </InternalSection>
  );
}

// ── 운영과 사업 정책 (Production · PwC 내부만) ──
function PolicyTile({ icon, label, title, text }: { icon: ReactNode; label: string | null; title: string | null; text: string | null }) {
  return (
    <li className="flex min-h-[181px] flex-col rounded-[16px] border border-nv-border bg-white p-6">
      <span className="flex items-center gap-[10px]">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-nv-brand-subtle text-nv-brand">{icon}</span>
        <span className="text-[14px] font-medium leading-[21px] text-nv-text-secondary">{label}</span>
      </span>
      <span className="mt-[14px] text-[22px] font-bold leading-[31px] text-nv-text-primary">{title}</span>
      {text && <span className="mt-1 text-[14px] leading-[21px] text-nv-text-secondary">{text}</span>}
    </li>
  );
}

export function PolicySection({ data }: { data: InternalPolicy }) {
  const opsIcons = [<NvFlagIcon key="f" />, <NvCopyIcon key="c" />, <NvShieldCheckIcon key="s" />];
  const bizIcons: Record<string, ReactNode> = { "판매 방식": <NvBoxIcon />, 가격: <NvPriceTagIcon />, "계약과 법적 제약": <NvLockOutlineIcon /> };
  return (
    <InternalSection id="policy" title="운영과 사업 정책" tag className="pt-10 xl:pt-10">
      <div className="flex flex-col">
        {data.operator && (
          <div className="flex items-center gap-4 rounded-[16px] bg-nv-brand-subtle px-7 py-6">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-white text-nv-brand">
              <NvPersonOutlineIcon size={20} />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-[13px] font-medium leading-5 text-nv-text-tertiary">운영 주체</span>
              <span className="flex flex-wrap items-center gap-x-3">
                <span className="text-[17px] font-bold leading-[26px] text-nv-text-primary">{data.operator.title}</span>
                {data.operator.text && <span className="text-[14px] leading-[21px] text-nv-text-secondary">{data.operator.text}</span>}
              </span>
            </span>
          </div>
        )}
        {data.ops.length > 0 && (
          <>
            <h3 className="mt-6 text-[15px] font-medium leading-[23px] text-nv-text-tertiary">운영</h3>
            <ul className="mt-3 grid gap-4 md:grid-cols-3">
              {data.ops.map((o, i) => (
                <PolicyTile key={i} icon={opsIcons[i % opsIcons.length]} label={o.label} title={o.title} text={o.text} />
              ))}
            </ul>
          </>
        )}
        {data.business.length > 0 && (
          <>
            <h3 className="mt-6 text-[15px] font-medium leading-[23px] text-nv-text-tertiary">사업 정책</h3>
            <ul className="mt-3 grid gap-4 md:grid-cols-3">
              {data.business.map((b) => (
                <PolicyTile key={b.label} icon={bizIcons[b.label]} label={b.label} title={b.title} text={b.text} />
              ))}
            </ul>
          </>
        )}
      </div>
    </InternalSection>
  );
}
